import { useCallback, useEffect, useRef } from "react";
import { TextButton } from "@root/shared/components/TextButton";

type Props = {
  initialConsultMode?: boolean;
};

const OPERATOR_KEYS = ["A", "B", "C"] as const;

// ERBフォームのカレンダー予約セクションと日程相談セクションを切り替えるボタン
// react_component_tagでERBに埋め込むためのブリッジコンポーネント
export const OnboardingConsultToggleButton = ({ initialConsultMode = false }: Props) => {
  const isConsultModeRef = useRef(initialConsultMode);

  const updateSubmitButton = useCallback((consultMode: boolean, checked: boolean) => {
    const submitButton = document.getElementById(
      "js-contract-submit-button",
    ) as HTMLButtonElement | null;
    if (!submitButton) return;
    const shouldDisable = !consultMode && !checked;
    submitButton.disabled = shouldDisable;
    submitButton.classList.toggle("c-btn--disabled-flat", shouldDisable);
  }, []);

  const handleClick = () => {
    const section = document.getElementById("js-onboarding-booking-section");
    const calendarMethodInput = document.getElementById(
      "js-booking-method-calendar",
    ) as HTMLInputElement | null;
    const consultMethodInput = document.getElementById(
      "js-booking-method-consult",
    ) as HTMLInputElement | null;
    const calendarBookedCheckbox = document.getElementById(
      "js-calendar-booked-checkbox",
    ) as HTMLInputElement | null;

    const newIsConsultMode = !isConsultModeRef.current;

    // 視覚的な切り替えはCSSクラスに委譲
    section?.classList.toggle("is-consult-mode", newIsConsultMode);

    if (newIsConsultMode) {
      // カレンダー → 日程相談
      if (consultMethodInput) consultMethodInput.checked = true;
      if (calendarBookedCheckbox) {
        calendarBookedCheckbox.checked = false;
        calendarBookedCheckbox.disabled = true;
      }
      // オペレーターのラジオを解除・disabled にしてiframeを閉じる
      OPERATOR_KEYS.forEach((key) => {
        const radio = document.getElementById(
          `js-calendar-person-${key}`,
        ) as HTMLInputElement | null;
        if (radio) {
          radio.checked = false;
          radio.disabled = true;
        }
        const iframe = document.getElementById(`js-calendar-iframe-${key}`);
        if (iframe) iframe.style.display = "none";
      });
    } else {
      // 日程相談 → カレンダー
      if (calendarMethodInput) calendarMethodInput.checked = true;
      // ID・パスワード発行希望日が入力済みの場合のみオペレーターのラジオを enabled に戻す
      const preferredDateInput = document.getElementById(
        "form_client_frontend_client_contract_web_entry_add_client_user_account_preferred_date",
      ) as HTMLInputElement | null;
      const preferredDateFilled = !!preferredDateInput?.value;
      OPERATOR_KEYS.forEach((key) => {
        const radio = document.getElementById(
          `js-calendar-person-${key}`,
        ) as HTMLInputElement | null;
        if (radio) radio.disabled = !preferredDateFilled;
      });
      if (calendarBookedCheckbox) calendarBookedCheckbox.disabled = false;
      // 日付入力済みの場合はオペレーターAを自動選択してカレンダーを表示する
      if (preferredDateFilled) {
        const radioA = document.getElementById("js-calendar-person-A") as HTMLInputElement | null;
        if (radioA) radioA.checked = true;
        document.querySelectorAll<HTMLElement>("[id^='js-calendar-iframe-']").forEach((el) => {
          const iframe = el;
          iframe.style.display = "none";
        });
        const iframeA = document.getElementById("js-calendar-iframe-A");
        if (iframeA) iframeA.style.display = "";
      }
    }

    // submit ボタンの disabled を更新
    updateSubmitButton(newIsConsultMode, calendarBookedCheckbox?.checked ?? false);
    isConsultModeRef.current = newIsConsultMode;
  };

  // 「直接日程を予約をする」リンクから handleClick を呼び出せるよう ref に保持
  const handleClickRef = useRef(handleClick);
  handleClickRef.current = handleClick;

  useEffect(() => {
    const calendarBookedCheckbox = document.getElementById(
      "js-calendar-booked-checkbox",
    ) as HTMLInputElement | null;

    updateSubmitButton(initialConsultMode, calendarBookedCheckbox?.checked ?? false);

    const handleCheckboxChange = (e: Event) => {
      updateSubmitButton(isConsultModeRef.current, (e.target as HTMLInputElement).checked);
    };

    calendarBookedCheckbox?.addEventListener("change", handleCheckboxChange);
    return () => {
      calendarBookedCheckbox?.removeEventListener("change", handleCheckboxChange);
    };
  }, [updateSubmitButton, initialConsultMode]);

  useEffect(() => {
    const backToCalendarLink = document.getElementById("js-back-to-calendar-link");
    if (!backToCalendarLink) return undefined;

    const onBackLinkClick = (e: MouseEvent) => {
      e.preventDefault();
      handleClickRef.current();
    };

    backToCalendarLink.addEventListener("click", onBackLinkClick);
    return () => backToCalendarLink.removeEventListener("click", onBackLinkClick);
  }, []);

  return (
    <TextButton size="small" rightIconName="arrow-light-right" onClick={handleClick}>
      予約可能な日時がない場合
    </TextButton>
  );
};
